import React, { useState } from "react";
import config from "lib/fr-config";
import CommonImageView from "presentational/common/CommonImageView";
import TextButtonView from "presentational/common/TextButtonView";
import TXT from "presentational/common/multinational/TXT";
import { getGradationCss } from "lib/commonUtil";
import { getDeviceType } from "lib/deviceInfo";
import { useHistory } from "react-router-dom";
import ItemDetailGuideTooltipView from "presentational/common/ItemDetailGuideTooltipView";
import styles from "./ItemDetailView.scss";
import { getFullUrl, getEssentialUrl } from "lib/urlQueryParser";
import classNames from "classnames/bind";
import PropTypes from "prop-types";

const cx = classNames.bind(styles);

/**
 * 아이템 주인의 성별 설정되지 않은경우,
 * DEFAULT_GENDER_VALUE 이 검색에 사용될 성별값의 기본값이 된다.
 */
const DEFAULT_GENDER_VALUE = "male";

const commonCss = config.css.commonDevice;
const fontCss = config.css.fontThickness;
const colorCss = config.css.color;
const itemColorList = config.itemColorList;
const desktopCss = config.css.desktop;
const mobileCss = config.css.mobile;

const {
  outerPaddingOfContent: PADDING,
  maxWidthOfContentWithoutPadding: MAX_WIDTH
} = commonCss.itemDetail;
const {
  normalPaddingRight: TXT_BTN_RIGHT_PADDING,
  normalPaddingTop: TXT_BTN_TOP_PADDING
} = commonCss.textButton;

ItemDetailView.propTypes = {
  mode: PropTypes.oneOf(["normal", "disableBtns", "demo", "guest"]).isRequired,
  displayDetailTooltip: PropTypes.bool,
  imageSrc: PropTypes.string.isRequired,
  category: PropTypes.string,
  brand: PropTypes.string,
  color: PropTypes.string,
  tag: PropTypes.string,
  gender: PropTypes.oneOf(["male", "female"]),
  cardView: PropTypes.bool
};

ItemDetailView.defaultProps = {
  displayDetailTooltip: false,
  cardView: false
};

function ItemDetailView(props) {
  const {
    mode,
    displayDetailTooltip,
    imageSrc,
    category,
    brand,
    color,
    tag,
    gender,
    cardView
  } = props;
  const [hasClickedAtLeastOnce, setHasClickedAtLeastOnce] = useState(false);

  const history = useHistory();

  /**
   * 하단 코드는 ItemDetailView 함수 밖에 배치하면 컴포넌트가 불리는 시점에서 주소를 확정하며,
   * 이로 인해 잘못된 주소를 가져오는 경우 발생된다
   */
  const URL_PREFIX_NORMAL = getEssentialUrl({ type: "search" })();
  const URL_PREFIX_GUEST = getEssentialUrl({
    type: "search",
    allowRefParam: true
  })();

  const getCssXL = () => {
    return {
      root: {
        backgroundColor: cardView ? colorCss.colorOfCard : undefined,
        padding: cardView ? PADDING : undefined,
        margin: cardView ? "auto" : undefined,
        maxWidth: MAX_WIDTH + PADDING * 2
      },
      leftSide: {
        width: `calc((100% - ${PADDING}px) / 2)`
      },
      rightSide: {
        width: `calc((100% - ${PADDING}px) / 2)`
      },
      textBtnWrapper: { whiteSpace: "normal", textAlign: "left" },
      colorBtnWrapper: {
        marginLeft: -TXT_BTN_RIGHT_PADDING,
        paddingTop: TXT_BTN_TOP_PADDING,
        paddingRight: TXT_BTN_RIGHT_PADDING,
        paddingBottom: TXT_BTN_TOP_PADDING,
        paddingLeft: TXT_BTN_RIGHT_PADDING,
        display: "inline-block"
      },
      colorBoxWrapper: {},
      clickableBtnWrapper: { cursor: "pointer" }
    };
  };

  const getCssXS = () => {
    return {
      root: {
        backgroundColor: cardView ? colorCss.colorOfCard : undefined,
        padding: cardView ? PADDING : undefined,
        margin: cardView ? "auto" : undefined,
        maxWidth: MAX_WIDTH + PADDING * 2
      },
      leftSide: {
        width: `calc((100% - ${PADDING}px) / 2)`
      },
      rightSide: {
        width: `calc((100% - ${PADDING}px) / 2)`
      },
      textBtnWrapper: { whiteSpace: "normal", textAlign: "left" },
      colorBtnWrapper: {
        marginLeft: -TXT_BTN_RIGHT_PADDING,
        paddingTop: TXT_BTN_TOP_PADDING,
        paddingRight: TXT_BTN_RIGHT_PADDING,
        paddingBottom: TXT_BTN_TOP_PADDING,
        paddingLeft: TXT_BTN_RIGHT_PADDING,
        display: "inline-block"
      },
      colorBoxWrapper: { marginTop: 4 },
      clickableBtnWrapper: { cursor: "pointer" }
    };
  };

  const css = getDeviceType() === "mobile" ? getCssXS() : getCssXL();

  const getDemoInput = type => {
    switch (type) {
      case "category":
        return <TXT name="btnCategoryDemoForItemDetail" />;
      case "brand":
        return <TXT name="btnBrandDemoForItemDetail" />;
      case "color":
        return "black";
      case "tag":
        return "how|amaive";
      default:
        console.log("error", type);
        console.error("error", type);
        return null;
    }
  };

  const addLinkElem = (elem, url, key) => {
    return (
      <div
        style={css.clickableBtnWrapper}
        onClick={handleClick(url)}
        key={key || undefined}
      >
        {elem}
      </div>
    );
  };

  const handleClick = url => async () => {
    /** setHasClickedAtLeastOnce 실행시 await 없다면
     * hasClickedAtLeastOnce 값이 변경되기 전에
     * history.push() 실행되기에 ItemDetailGuideTooltipView 툴팁의
     * 카운트가 증가되지 않는다.
     * <반드시 await 제거하지 말자> */
    await setHasClickedAtLeastOnce(true);
    history.push(url);
  };

  const getTextBtnElem = (type, text) => {
    const FONT_SIZE_OF_CAT_FOR_DESKTOP =
      desktopCss.fontSizeOfItemDetail.normalText;
    const FONT_SIZE_OF_BRAND_FOR_DESKTOP =
      desktopCss.fontSizeOfItemDetail.brandText;
    const FONT_SIZE_OF_TAG_FOR_DESKTOP =
      desktopCss.fontSizeOfItemDetail.normalText;

    const FONT_SIZE_OF_CAT_FOR_MOBILE =
      mobileCss.fontSizeOfItemDetail.normalText;
    const FONT_SIZE_OF_BRAND_FOR_MOBILE =
      mobileCss.fontSizeOfItemDetail.brandText;
    const FONT_SIZE_OF_TAG_FOR_MOBILE =
      mobileCss.fontSizeOfItemDetail.normalText;

    const deviceType = getDeviceType();

    let disableLeftMargin,
      disableTopMargin,
      fontSize,
      key,
      paddingSize,
      color,
      fontWeight,
      label;
    switch (type) {
      case "category":
        fontSize =
          deviceType === "mobile"
            ? FONT_SIZE_OF_CAT_FOR_MOBILE
            : FONT_SIZE_OF_CAT_FOR_DESKTOP;
        disableLeftMargin = true;
        disableTopMargin = true;
        color = colorCss.colorOfSecondary;
        key = undefined;
        paddingSize = undefined;
        fontWeight = fontCss.regular;
        label = text;
        break;
      case "brand":
        fontSize =
          deviceType === "mobile"
            ? FONT_SIZE_OF_BRAND_FOR_MOBILE
            : FONT_SIZE_OF_BRAND_FOR_DESKTOP;
        disableLeftMargin = true;
        disableTopMargin = false;
        color = colorCss.colorOfMainText;
        key = undefined;
        paddingSize = undefined;
        fontWeight = fontCss.medium;
        label = text;
        break;
      case "tag":
        fontSize =
          deviceType === "mobile"
            ? FONT_SIZE_OF_TAG_FOR_MOBILE
            : FONT_SIZE_OF_TAG_FOR_DESKTOP;
        disableLeftMargin = false;
        disableTopMargin = false;
        color = colorCss.colorOfMainText;
        key = text;
        paddingSize = "small";
        fontWeight = fontCss.regular;
        label = `#${text}`;
        break;

      default:
        console.log("error");
        console.error("error");
        break;
    }

    const btnElement = (
      <TextButtonView
        disableLeftMargin={disableLeftMargin}
        disableTopMargin={disableTopMargin}
        fontSize={fontSize}
        fontColor={color}
        key={key}
        paddingSize={paddingSize}
        fontWeight={fontWeight}
        onClick={() => null}
      >
        <span style={css.textBtnWrapper}>{label}</span>
      </TextButtonView>
    );

    if (type === "category") {
      return (
        <ItemDetailGuideTooltipView
          disabled={!displayDetailTooltip}
          hasClickedAtLeastOnce={hasClickedAtLeastOnce}
        >
          <span>{btnElement}</span>
        </ItemDetailGuideTooltipView>
      );
    } else {
      return btnElement;
    }
  };

  const getColorBlockBtnElem = color => {
    return (
      <div style={css.colorBoxWrapper}>
        <div style={css.colorBtnWrapper}>
          <div key={color} className={cx("color-btn-border")}>
            <div
              className={cx("color-block", {
                "white-block": color === "white"
              })}
              style={{
                background: getGradationCss(itemColorList[color])
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const categoryBtn = () => {
    const btnText =
      mode === "demo" ? (
        getDemoInput("category")
      ) : (
        <TXT textType="category" categoryType="itemLabelName" name={category} />
      );
    const returnBtn = getTextBtnElem("category", btnText);

    let url;
    switch (mode) {
      case "normal":
        url = getFullUrl({
          url: URL_PREFIX_NORMAL,
          queryObj: { category, gender: gender || DEFAULT_GENDER_VALUE }
        });
        return addLinkElem(returnBtn, url);
      case "guest":
        url = getFullUrl({
          url: URL_PREFIX_GUEST,
          queryObj: { category, gender: gender || DEFAULT_GENDER_VALUE }
        });
        return addLinkElem(returnBtn, url);
      case "disableBtns":
      case "demo":
        return returnBtn;

      default:
        console.log("error");
        console.error("error");
        return null;
    }
  };

  const brandBtn = () => {
    const btnText = mode === "demo" ? getDemoInput("brand") : brand;
    const returnBtn = getTextBtnElem("brand", btnText);

    let url;
    switch (mode) {
      case "normal":
        url = getFullUrl({
          url: URL_PREFIX_NORMAL,
          queryObj: { category, gender: gender || DEFAULT_GENDER_VALUE, brand }
        });
        return addLinkElem(returnBtn, url);
      case "guest":
        url = getFullUrl({
          url: URL_PREFIX_GUEST,
          queryObj: { category, gender: gender || DEFAULT_GENDER_VALUE, brand }
        });
        return addLinkElem(returnBtn, url);
      case "disableBtns":
      case "demo":
        return returnBtn;

      default:
        console.log("error");
        console.error("error");
        return null;
    }
  };

  const colorBtns = () => {
    if (mode !== "demo" && !color) return null;

    const colorInfo = mode === "demo" ? getDemoInput("color") : color;
    const returnBtn = getColorBlockBtnElem(colorInfo);

    let url;
    switch (mode) {
      case "normal":
        url = getFullUrl({
          url: URL_PREFIX_NORMAL,
          queryObj: { category, gender: gender || DEFAULT_GENDER_VALUE, color }
        });
        return addLinkElem(returnBtn, url);
      case "guest":
        url = getFullUrl({
          url: URL_PREFIX_GUEST,
          queryObj: { category, gender: gender || DEFAULT_GENDER_VALUE, color }
        });
        return addLinkElem(returnBtn, url);
      case "disableBtns":
      case "demo":
        return returnBtn;

      default:
        console.log("error");
        console.error("error");
        return null;
    }
  };

  const tagBtns = () => {
    if (mode !== "demo" && !tag) return null;

    const tagFullString = mode === "demo" ? getDemoInput("tag") : tag;
    const tagArr = tagFullString.split("|");

    return tagArr.map(tag => {
      let url;
      switch (mode) {
        case "normal":
          url = getFullUrl({
            url: URL_PREFIX_NORMAL,
            queryObj: { category, gender: gender || DEFAULT_GENDER_VALUE, tag }
          });
          return addLinkElem(getTextBtnElem("tag", tag), url, tag);
        case "guest":
          url = getFullUrl({
            url: URL_PREFIX_GUEST,
            queryObj: { category, gender: gender || DEFAULT_GENDER_VALUE, tag }
          });
          return addLinkElem(getTextBtnElem("tag", tag), url, tag);
        case "disableBtns":
        case "demo":
          return getTextBtnElem("tag", tag);

        default:
          console.log("error");
          console.error("error");
          return null;
      }
    });
  };

  return (
    <div className={cx("root")} style={css.root}>
      <div style={css.leftSide}>
        <CommonImageView
          imageSrc={imageSrc}
          imageRatio={config.itemImage.RATIO}
        />
      </div>
      <div className={cx("right-side")} style={css.rightSide}>
        <div className={cx("right-content")}>
          <div className={cx("right-unit")}>
            <div>{categoryBtn()}</div>
          </div>
          <div className={cx("right-unit")}>
            <div>{brandBtn()}</div>
          </div>
          <div className={cx("right-unit", "color-unit")}>
            <div>{colorBtns()}</div>
          </div>
          <div className={cx("right-unit")}>
            <div>{tagBtns()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ItemDetailView;
